import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Hook for managing a cooldown timer (e.g., for resend buttons).
 *
 * @param durationSeconds - The cooldown duration in seconds
 * @returns Object with cooldownSeconds, startCooldown function, and isOnCooldown boolean
 *
 * @example
 * const { cooldownSeconds, startCooldown, isOnCooldown } = useCooldown(30);
 *
 * // Start cooldown after sending code
 * const handleSend = async () => {
 *   await sendCode();
 *   startCooldown();
 * };
 *
 * // Use in UI
 * <Button disabled={isOnCooldown}>
 *   {isOnCooldown ? `Resend in ${cooldownSeconds}s` : 'Resend'}
 * </Button>
 */
function useCooldown(durationSeconds) {
    const [cooldownSeconds, setCooldownSeconds] = useState(0);
    const cooldownInterval = useRef(null);
    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (cooldownInterval.current) {
                clearInterval(cooldownInterval.current);
            }
        };
    }, []);
    const startCooldown = useCallback(() => {
        setCooldownSeconds(durationSeconds);
        // Clear any existing interval
        if (cooldownInterval.current) {
            clearInterval(cooldownInterval.current);
        }
        cooldownInterval.current = setInterval(() => {
            setCooldownSeconds((prev) => {
                if (prev <= 1) {
                    if (cooldownInterval.current) {
                        clearInterval(cooldownInterval.current);
                        cooldownInterval.current = null;
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [durationSeconds]);
    return {
        cooldownSeconds,
        startCooldown,
        isOnCooldown: cooldownSeconds > 0,
    };
}

export { useCooldown };
//# sourceMappingURL=useCooldown.js.map
